import { FeaturedArticles } from "components/blog/FeaturedArticles";
import { CustomVideoPlayer } from "components/common/CustomVideoPlayer/CustomVideoPlayer";
import { ExternalVideo } from "components/common/Dato/ExternalVideo";
import { Faq } from "components/common/Faq/Faq";
import { QuickLinksBlock } from "components/common/QuickLinksBlock/QuickLinksBlock";
import Hero from "components/Hero/Hero";
import { ProductsSlider } from "components/homepage/BestSellerSection/ProductsSlider";
import { BigTilesSection } from "components/homepage/BigTilesSection/BigTilesSection";
import { HeroImageSection } from "components/homepage/HeroImageSection";
import { SmallTilesSection } from "components/homepage/SmallTilesSection/SmallTilesSection";
import { SplitHeroWithTextAndImage } from "components/homepage/SplitHeroWithTextAndImage/SplitHeroWithTextAndImage";
import { ColumnContentBlock } from "components/ProductDetails/Content/ColumnContentBlock/ColumnContentBlock";
import { RichTextBlock } from "components/ProductDetails/Content/RichTextBlock/RichTextBlock";
import { SplitContentBlock } from "components/ProductDetails/Content/SplitContentBlock/SplitContentBlock";
import { Document, isParagraph } from "datocms-structured-text-utils";
import { FC } from "react";
import { Image } from "react-datocms/image";
import { renderNodeRule, StructuredText } from "react-datocms/structured-text";
import {
  ArticleBySlugQuery,
  BigTilesComponentFragment,
  ColumnContentComponentFragment,
  ExternalVideoComponentFragment,
  FeaturedBlogPostComponentFragment,
  HeroImageComponentFragment,
  HomepageHeroComponentFragment,
  LinksAsCardsComponentFragment,
  ProductsSliderComponentFragment,
  ResponsiveImage,
  RichTextComponentFragment,
  SmallTilesComponentFragment,
  SplitContentComponentFragment,
  SplitHeroComponentFragment,
  TabFaqComponentFragment,
} from "services/datocms/generated";
import st from "./blog-post-content.module.scss";
import FeaturedProduct from "./FeaturedProduct";

interface Props {
  article: ArticleBySlugQuery["article"];
}

type ImageBlock = {
  id: string;
  __typename: "ImageRecord";
  image: {
    responsiveImage: ResponsiveImage;
  };
};

type VideoBlock = {
  id: string;
  __typename: "VideoRecord";
  video: {
    video: {
      muxPlaybackId: string;
      thumbnailUrl: string;
      streamingUrl: string;
    };
  };
};

type StructuredTextBlocks = ImageBlock | VideoBlock;

type StructuredTextLinks =
  | HomepageHeroComponentFragment
  | ColumnContentComponentFragment
  | HeroImageComponentFragment
  | RichTextComponentFragment
  | ProductsSliderComponentFragment
  | LinksAsCardsComponentFragment
  | SplitContentComponentFragment
  | SplitHeroComponentFragment
  | BigTilesComponentFragment
  | SmallTilesComponentFragment
  | FeaturedBlogPostComponentFragment
  | ExternalVideoComponentFragment
  | TabFaqComponentFragment;

const BlogPostContent: FC<Props> = ({ article }) => {
  return (
    <div className={st.container}>
      {article?.heroVisual?.responsiveImage && (
        <div className={st.contentHero}>
          <Image
            data={article?.heroVisual?.responsiveImage}
            layout="fill"
            objectFit="contain"
          />
        </div>
      )}
      <div className={st.contentContainer}>
        <div className={st.textContainer}>
          <StructuredText<StructuredTextBlocks, StructuredTextLinks>
            data={article?.content as unknown as Document}
            renderInlineRecord={({ record }) => {
              switch (record.__typename) {
                case "HomepageHeroComponentRecord":
                  return <Hero key={record.id} data={record} />;
                case "ColumnContentComponentRecord":
                  return (
                    <ColumnContentBlock key={record.id} content={record} />
                  );
                case "HeroImageComponentRecord":
                  return <HeroImageSection key={record.id} content={record} />;
                case "RichTextComponentRecord":
                  return <RichTextBlock key={record.id} content={record} />;
                case "ProductsSliderComponentRecord":
                  return <ProductsSlider key={record.id} data={record} />;
                case "LinksAsCardsComponentRecord":
                  return <QuickLinksBlock key={record.id} data={record} />;
                case "SplitContentComponentRecord":
                  return <SplitContentBlock key={record.id} content={record} />;
                case "SplitHeroComponentRecord":
                  return (
                    <SplitHeroWithTextAndImage key={record.id} data={record} />
                  );
                case "BigTilesComponentRecord":
                  return <BigTilesSection key={record.id} content={record} />;
                case "SmallTilesComponentRecord":
                  return <SmallTilesSection key={record.id} content={record} />;
                case "FeaturedBlogPostComponentRecord":
                  return <FeaturedArticles key={record.id} content={record} />;
                case "ExternalVideoRecord":
                  return <ExternalVideo key={record.id} content={record} />;
                case "FaqComponentRecord":
                  return <Faq key={record.id} data={record} />;
                default:
                  return null;
              }
            }}
            renderBlock={({ record }) => {
              switch (record.__typename) {
                case "ImageRecord":
                  return (
                    <Image
                      layout="responsive"
                      objectFit="contain"
                      data={record.image.responsiveImage}
                    />
                  );
                case "VideoRecord":
                  return <CustomVideoPlayer data={record.video.video} />;
                default:
                  return null;
              }
            }}
            customNodeRules={[
              renderNodeRule(
                isParagraph,
                ({ adapter: { renderNode }, node, children, key }) => {
                  if (node.children[0].type === "inlineItem") {
                    return <div key={key}>{children}</div>;
                  } else {
                    return renderNode("p", { key }, children);
                  }
                },
              ),
            ]}
          />
        </div>
        {article?.featuredProducts && article?.featuredProducts.length > 0 && (
          <div className={st.featuredProductsContainer}>
            {article?.featuredProducts.map((p, idx) => (
              <FeaturedProduct
                key={idx}
                heroUrl={p.productImages[0]?.url || ""}
                beforeImageText={p.title || ""}
                afterImageText={p.shortDescription || ""}
                productLink={`/${p.category?.slug}/${p.slug}`}
                direction={
                  article?.featuredProductDirection as "row" | "column"
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPostContent;
